import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import {
  API, withCreds, selectCls, smallInputCls, btnSecondary, ORDER_STATUSES,
  PageHeader, Spinner, StatusBadge, money, fmtDate, errMsg, downloadCsv,
} from '../../components/admin/AdminUI';
import { ShoppingBag, Search, Download, Truck, Check, X } from 'lucide-react';
import Select from '../../components/FlatSelect';

const TABS = [
  ['', 'All'], ['pending,paid,processing', 'To ship'], ['shipped', 'Shipped'], ['delivered', 'Delivered'],
  ['cancelled,refunded', 'Cancelled / refunded'],
];
const PAGE_SIZE = 25;

export default function AdminOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: searchParams.get('status') || '', search: '', date_from: '', date_to: '' });
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders`, { params: { ...filters, page, limit: PAGE_SIZE }, ...withCreds });
      setOrders(r.data.orders);
      setTotal(r.data.total);
      setCounts(r.data.status_counts || {});
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); setSelected([]); };
  const tabCount = (statuses) => (statuses ? statuses.split(',').reduce((n, s) => n + (counts[s] || 0), 0) : Object.values(counts).reduce((a, b) => a + b, 0));
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applyBulk = async () => {
    if (!bulkStatus) return;
    if (['cancelled', 'refunded'].includes(bulkStatus) && !window.confirm(`Mark ${selected.length} order(s) as ${bulkStatus}? Stock will be returned to inventory. Card payments are NOT refunded by this. Use Refund on an order for that.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/orders/bulk-status`, { order_ids: selected, status: bulkStatus, notify }, withCreds);
      alert(`Updated ${r.data.changed} order(s).`);
      setSelected([]); setBulkStatus('');
      fetchOrders();
    } catch (e) { alert(errMsg(e)); }
    finally { setBusy(false); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === orders.length ? [] : orders.map(o => o.id));

  return (
    <AdminLayout>
      <PageHeader title="Orders" subtitle={`${total} order${total === 1 ? '' : 's'}`}>
        <button onClick={() => downloadCsv('/admin/orders/export', filters).catch(e => alert(errMsg(e)))} className={btnSecondary}><Download className="w-4 h-4" /> Export CSV</button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map(([value, label]) => (
          <button key={label} onClick={() => setFilter('status', value)} data-testid={`orders-tab-${label}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${filters.status === value ? 'bg-honey/10 text-honey-700 border-honey/30' : 'bg-ink/[0.04] text-ink/60 border-ink/10 hover:text-ink'}`}>
            {label} <span className="ml-1 text-xs opacity-70">{tabCount(value)}</span>
          </button>
        ))}
      </div>

      <div className="admin-card rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
          <input value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && setFilter('search', searchText)}
            onBlur={() => searchText !== filters.search && setFilter('search', searchText)}
            placeholder="Order #, customer, email, SKU, tracking…"
            className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-10 pr-4 py-2 text-ink text-sm focus:outline-none focus:border-honey" />
        </div>
        <label className="text-ink/55 text-xs flex items-center gap-2">From
          <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} className={smallInputCls} style={{ colorScheme: 'light' }} />
        </label>
        <label className="text-ink/55 text-xs flex items-center gap-2">To
          <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} className={smallInputCls} style={{ colorScheme: 'light' }} />
        </label>
        {(filters.date_from || filters.date_to || filters.search) && (
          <button onClick={() => { setSearchText(''); setFilters(f => ({ ...f, search: '', date_from: '', date_to: '' })); setPage(1); }} className="text-ink/55 hover:text-ink text-xs flex items-center gap-1">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        {selected.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap w-full border-t border-ink/10 pt-3 mt-1">
            <span className="text-honey-700 text-sm font-bold">{selected.length} selected</span>
            <Select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)} className={selectCls}>
              <option value="">Change status to…</option>
              {ORDER_STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
            <label className="flex items-center gap-2 text-ink/60 text-xs cursor-pointer">
              <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="accent-honey" /> Email customers
            </label>
            <button onClick={applyBulk} disabled={!bulkStatus || busy} className="flex items-center gap-1 px-3 py-1.5 bg-honey text-black rounded-lg text-sm font-bold disabled:opacity-40">
              <Check className="w-3 h-3" /> Apply
            </button>
            <button onClick={() => setSelected([])} className="text-ink/45 hover:text-ink ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <div className="admin-card rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-ink/10">
            <tr className="text-ink/55 text-xs uppercase tracking-widest">
              <th className="p-3 w-8"><input type="checkbox" checked={selected.length === orders.length && orders.length > 0} onChange={toggleAll} className="accent-honey" /></th>
              <th className="text-left p-3">Order</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Items</th>
              <th className="text-left p-3">Total</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Shipping</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {loading ? (
              <tr><td colSpan={7}><Spinner /></td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12">
                <ShoppingBag className="w-8 h-8 text-ink/30 mx-auto mb-2" />
                <p className="text-ink/45 text-sm">No orders found</p>
              </td></tr>
            ) : orders.map(o => (
              <tr key={o.id} onClick={() => navigate(`/admin/orders/${o.id}`)} className="text-ink/75 hover:bg-ink/[0.04] cursor-pointer" data-testid={`order-row-${o.id}`}>
                <td className="p-3" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggleSelect(o.id)} className="accent-honey" />
                </td>
                <td className="p-3">
                  <p className="text-honey-700 font-mono text-xs font-bold">{o.order_number}</p>
                  <p className="text-ink/45 text-xs">{fmtDate(o.created_at)}</p>
                </td>
                <td className="p-3">
                  <p className="text-ink/85 truncate max-w-[200px]">{o.customer_name || o.shipping_address?.name}</p>
                  <p className="text-ink/45 text-xs truncate max-w-[200px]">{o.customer_email}</p>
                </td>
                <td className="p-3 text-ink/60 text-xs">{o.items?.reduce((n, i) => n + i.quantity, 0)} item(s)</td>
                <td className="p-3 font-bold text-ink">{money(o.total)}</td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
                <td className="p-3 text-xs">
                  {o.tracking_number ? (
                    <span className="flex items-center gap-1 text-ink/60"><Truck className="w-3 h-3" /> {o.carrier || 'Tracking'}{o.tracking_status ? ` · ${o.tracking_status.toLowerCase().replace('_', ' ')}` : ''}</span>
                  ) : ['pending', 'paid', 'processing'].includes(o.status) ? <span className="text-amber-600/80">Needs label</span> : <span className="text-ink/30">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className={btnSecondary}>Prev</button>
          <span className="text-ink/55 text-sm">Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className={btnSecondary}>Next</button>
        </div>
      )}
    </AdminLayout>
  );
}
